<template>
    <!-- AV.W9.a — the complete-screen headline cluster: the golden
         CompleteBadge mark paired with the gold-shimmer "Complete!"
         headline. Carved out of `SpeedtestResults.vue`'s `complete-wrap`
         (template 271-308) as a SEPARABLE unit — it owns a single clean
         prop boundary (`:shown`) and a self-contained style domain (the
         `.complete-headline-cluster` row/stack composition + the
         `.complete-headline-host` AN-D5 type-clamp).

         The root carries `complete-headline-cluster complete-reveal`:
         Vue stamps a child's ROOT element with the PARENT's `data-v-*`
         scope id, so `SpeedtestResults`'s composition-root reveal gate
         (`.results-card[data-morph-resolved="true"] .complete-reveal`)
         still reaches this cluster — identical to how it reaches
         `<ResultStack class="complete-reveal …">`. The choreography
         gate stays the parent's (the clock-owner hooks at the
         composition root); this unit owns only its own appearance. -->
    <div v-if="shown" class="complete-headline-cluster complete-reveal">
        <!-- Golden complete logo — gold disc + ring + check, glowing
             aura. AV.W19 hidden-celebration fix: the badge's staged enter
             choreography plays on `playEnter` (the REVEAL clock —
             `data-morph-resolved`), NOT on `shown` (the `isComplete` mount
             edge). The cluster MOUNTS on `shown` so the headline is in the
             DOM at the completion edge, but the seal's ~840ms disc / ring /
             check draw fires only WHEN the reveal opens (the container fades
             `opacity 0 → 1`) — so the celebration plays in full view, not
             under the hidden `opacity:0` container. When `personalBest`, the
             seal sweeps an extra triumphant gold arc (B2 — forwarded
             straight through from the parent's verdict). -->
        <CompleteBadge :shown="playEnter" :personal-best="personalBest" />
        <!-- Celebratory gold-shimmer headline. `<CompleteHeadline>`
             carries the D8 glyph-clipped glow + D9 one-shot shimmer +
             PRM bracket (the unified primitive shared with the survey
             thank-you headline).

             AN-D5 — the word is demoted from the celebration-peak rung
             (`--text-celebration-headline-size` ≈144px) to the
             `--type-display-4` rung (≤86px peak) with a lower ~2.5rem
             mobile floor (collision-safe with the gold mark — plan §10).
             The hero DL/UL numbers (~7rem) are the loudest element on
             the screen; "Complete!" reads as the SECOND voice — the
             punctuation beside the gold mark, not a competing display
             headline. -->
        <CompleteHeadline text="Complete!" class="complete-headline-host" />
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import CompleteBadge from "./CompleteBadge.vue";
import CompleteHeadline from "./CompleteHeadline.vue";

// Two-edge prop boundary (AV.W19). `shown` is the MOUNT edge (the
// `isComplete` completion edge — gates the cluster's `v-if` so the
// headline is in the DOM at completion). `playEnter` is the REVEAL clock
// (`data-morph-resolved` — the morph's own resolution) that triggers the
// `<CompleteBadge>` staged enter, so the seal draw plays WHEN the reveal
// opens (visible), not under the `opacity:0` container the mount edge
// sits behind. `playEnter` falls back to `shown` so a host that has no
// separate reveal clock (the byte-stable default) keeps the old behaviour.
const props = withDefaults(
    defineProps<{
        shown: boolean;
        /** The reveal clock — drives the badge's staged enter. Defaults to
         *  `shown` (byte-stable for hosts without a separate reveal clock). */
        playEnter?: boolean;
        /** B2 — forwarded to `<CompleteBadge>`: when true (this run beat the
         *  stored best), the seal sweeps its triumphant gold arc. The verdict
         *  is resolved at the parent composition root (off the completion
         *  edge); this cluster only relays it. Defaults false. */
        personalBest?: boolean;
    }>(),
    { playEnter: undefined, personalBest: false },
);

// `playEnter ?? shown` — when the host omits the reveal clock, the badge
// enter keys on the mount edge (the pre-W19 behaviour).
const playEnter = computed(() => props.playEnter ?? props.shown);
</script>

<style scoped>
/* Headline cluster — the golden complete logo paired with the
   "Complete!" word. Row layout at ≥ 28rem container width so the mark
   sits beside the word; stacks below it on narrow panels. The
   `@container` query resolves against the same `.metric-col` /
   card-level inline-size container the parent established (unchanged by
   the carve — the cluster sits in the same DOM position). */
.complete-headline-cluster {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: clamp(0.5rem, 1.5cqi, 0.875rem);
    flex-shrink: 0;
}
@container (min-width: 28rem) {
    .complete-headline-cluster {
        flex-direction: row;
        gap: clamp(0.875rem, 2.5cqi, 1.5rem);
    }
}

/* "Complete!" headline — AN-D5 demotion + W22.1 register unification
   (DDR-W22-HERO §"one register").

   The word leaves the celebration-peak rung (`--text-celebration-
   headline-size` ≈144px ceiling) and steps DOWN to the confirmation
   register so the DOWNLOAD protagonist number (≤14rem — see ResultStack)
   is the loudest element on screen; "Complete!" reads as the SECOND
   voice — the gold-clipped punctuation beside the hero seal, not a
   competing display headline. The shared celebration token stays at its
   peak for `<ThankYou>` (which earns its own celebration moment); only
   the speedtest-complete surface demotes here, per the SPEC's "the word
   confirms; the number shouts" inversion.

   W22 COLLAPSES THE cqi-vs-vw CLAMP SPLIT. The prior demotion drove the
   word off a `vw` clamp on the (now-stale) premise that the headline sat
   OUTSIDE a container-query host. It does NOT: the AP-Pγ card-level
   `container-type: inline-size` + `.metric-col`'s own inline-size
   container both resolve as nearer CQ hosts (witnessed:
   `.gold-headline`'s nearest container is `.metric-col`). So the seal
   (a `cqi` clamp) and the word (a `vw` clamp) read on DIFFERENT bases —
   a split that lets the lockup ratio drift across viewports. Unifying
   the word onto the SAME `cqi` basis the seal uses collapses the split:
   both lockup members now scale in lockstep against the card container,
   so "Complete!" + the seal stay ONE confirmation register at every
   width. Peak ≈ 5.382rem (`--type-display-4` band); floor 2.5rem,
   collision-safe with the gold mark on the narrow COLUMN layout. */
.complete-headline-host {
    --complete-headline-size: clamp(2.5rem, 9cqi, 5.382rem);
}
</style>
